import { Alert, Button, Checkbox, Col, Form, FormInstance, Input, InputNumber, message, Row, Select, Collapse, Divider } from "antd"
import TextArea from "antd/es/input/TextArea"
import React, { useEffect, useRef, useState } from "react"
import { useRegister } from "../../contexts/RegisterContext"
import { formatInputNumber, parseInputNumber, roundValue } from "../../utils"
import { MinusCircleOutlined, BarcodeOutlined, SettingOutlined } from "@ant-design/icons"
import BarcodeScannerModal from "../modals/BarcodeScannerModal"
import { useBarcodeScanner } from "../../hooks/useBarcodeScanner"
import { useConfig } from "../../contexts/ConfigContext"
import { modules } from "../../config/pages"
import { TElementModuleOptions, TModuleOptions } from "../../models/storageModel"
import ItemImageUploader from "../common/ItemImageUploader"

type formProps = {
  form: FormInstance
  getFormTaxes: (values:any, type:"sell" | "purchase") => Partial<TElementModuleOptions>[]
}

function RegisterServiceForm({form,getFormTaxes}:Readonly<formProps>){
  const { Option } = Select
  const {register} = useRegister()
  const {config} = useConfig()
  let {currentStorageItem: currentRegister} = register
  const mod = config.module
  // Use refs to maintain unique counters that never reset
  const idCounter = useRef(-1);
  const getNextId = () => --idCounter.current;
  
  const categories = register.categoryOptions.filter(el => {
    if (mod === modules.EditStorageItem && el.key === currentRegister?.category) {
      return true;
    }
    return el.show !== false;
  })
  const sellTaxesOptions = register.taxesOptions.filter(el => {
    if (mod === modules.EditStorageItem) {
      const isAssigned = currentRegister?.module_options?.some(opt => opt.option_id === el.id && opt.sub_type === "SELL_TAX");
      if (isAssigned) return true;
    }
    return el.show !== false;
  })
  const purchaseTaxesOptions = register.taxesOptions.filter(el => {
    if (mod === modules.EditStorageItem) {
      const isAssigned = currentRegister?.module_options?.some(opt => opt.option_id === el.id && opt.sub_type === "PURCHASE_TAX");
      if (isAssigned) return true;
    }
    return el.show !== false;
  })

  const [sellTaxes, setSellTaxes] = useState([{id:-1}]);
  const [purchaseTaxes, setPurchaseTaxes] = useState([{id:-1}]);
  const [otherValues, setOtherValues] = useState([{id:-1}]);
  
  const [sellTaxesWarning, setSellTaxesWarning] = useState(false)
  const [purchaseTaxesWarning, setPurchaseTaxesWarning] = useState(false)
  const [otherValuesWarning, setOtherValuesWarning] = useState(false)

  const [isScannerOpen, setIsScannerOpen] = useState(false);

  const formImages = Form.useWatch("images", form);

  // Handle global barcode scanner events
  const handleBarcodeScanned = (scannedBarcode: string) => {
    const currentBarcode = form.getFieldValue("barcode");
    if (!currentBarcode) {
      form.setFieldsValue({ barcode: scannedBarcode });
    }
  };

  useBarcodeScanner({ onScan: handleBarcodeScanned, isActive: !isScannerOpen });

  useEffect(()=>{
    //Asignar array de impuestos  y otros valores cuando se va a editar un registro
    const sellTaxes = currentRegister?.module_options?.filter(el => el.sub_type === "SELL_TAX").map(el => ({id:el.id || -1})) || []
    const purchaseTaxes = currentRegister?.module_options?.filter(el => el.sub_type === "PURCHASE_TAX").map(el => ({id:el.id || -1})) || []
    const otherValues = currentRegister?.other_values?.map(el => ({id:el.id || -1})) || []
    if(mod === modules.EditStorageItem && sellTaxes?.length) {
      setSellTaxes(sellTaxes)
    }
    if(mod === modules.EditStorageItem && purchaseTaxes?.length) {
      setPurchaseTaxes(purchaseTaxes)
    }
    if(mod === modules.EditStorageItem && otherValues?.length) {
      setOtherValues(otherValues)
    }
    if(mod === modules.EditStorageItem && currentRegister?.sell_price){
      handlePriceChange("sell")
    }
    if(mod === modules.EditStorageItem && currentRegister?.purchase_price){
      handlePriceChange("purchase")
    }
  },[register,mod])

  const scanForEmptyFields = (data:any,setData:any,setShoWarning:any,key:string)=>{
    const values = form.getFieldsValue()
    console.log("Values: ",values)
    let emptyValues = false
    for(const str in values){
      emptyValues = str.includes(key) && values[str] === undefined
      if(emptyValues) {
        break
      }
    }
    if(emptyValues){
      setShoWarning(true)
    }else{
      setData([...data, {id: getNextId() }]);
      setShoWarning(false)
    }
  }

  const removeSellTaxes = (id:number) => {
    if(id > 0){
      message.error("No se puede eliminar un impuesto que ya se encuentra registrado")
      return
    }
    const filterResult = sellTaxes.filter(item => item.id !== id)
    const result = filterResult.length ? filterResult : [{id: getNextId()}]
    form.setFieldsValue({
      [`sell_taxes_${id}`]: undefined,
      [`sell_taxes_active_${id}`]: undefined,
      sell_price_net: undefined,
      sell_price_full: undefined
    })
    setSellTaxes(result)
  }

  const removePurchaseTaxes = (id:number) => {
    if(id > 0){
      message.error("No se puede eliminar un impuesto que ya se encuentra registrado")
      return
    }
    const filterResult = purchaseTaxes.filter(item => item.id !== id)
    const result = filterResult.length ? filterResult : [{id: getNextId()}]
    form.setFieldsValue({
      [`purchase_taxes_${id}`]: undefined,
      [`purchase_taxes_active_${id}`]: undefined,
      purchase_price_net: undefined,
      purchase_price_full: undefined
    })
    setPurchaseTaxes(result)
  }

  const removeOtherValues = (id:number) => {
    if(id > 0){
      message.error("No se puede eliminar un campo que ya se encuentra registrado")
      return
    }
    const filterResult = otherValues.filter(item => item.id !== id)
    const result = filterResult.length ? filterResult : [{id: getNextId()}]
    form.setFieldsValue({
      [`otherValues_type_${id}`]: undefined,
      [`otherValues_value_${id}`]: undefined,
      [`otherValues_visible_${id}`]: undefined,
    })
    setOtherValues(result)
  }

  const allValues = Form.useWatch([], form);
  const checkedList = allValues?.registerOptions || ["SALE", "PURCHASE"];
  const sellDiscountValue = Form.useWatch("sell_discount", form);
  const purchaseDiscountValue = Form.useWatch("purchase_discount", form);

  const disabledOption = (option:string, field:"sell_taxes" | "purchase_taxes") => {
    const values = form.getFieldsValue()
    for(const key in values){
      if(key.startsWith(field) && !key.includes("active") && values[key] === option){
        return true
      }
    }
    return false
  }

  const isFieldDisabled = (id: number, prefix: string) => {
    if (id <= 0) return false;
    return allValues?.[`${prefix}_active_${id}`] === false || allValues?.[`${prefix}_visible_${id}`] === false;
  };

  const handlePriceChange = (type: "sell" | "purchase", overrides: Record<string, any> = {}) => {
    const formValues = { ...form.getFieldsValue(), ...overrides }
    const value = formValues[`${type}_price`] || 0
    const keyFullPrice = type === "sell" ? "sell_price_full" : "purchase_price_full"
    const keyNetPrice = type === "sell" ? "sell_price_net" : "purchase_price_net"
    const keyDiscount = type === "sell" ? "sell_discount" : "purchase_discount"
    const AllTaxes = getFormTaxes({...formValues},type)
                    .filter(el => el.is_active)
                    .map(el => register.taxesOptions.find(tax => tax.id === el.option_id) as TModuleOptions)
                    .filter(Boolean)
    const rateTax = AllTaxes.filter(el => el.type === "TAX_RATE").reduce((sum, el) => sum + (el.numeric_value || 0), 0)
    const fixedTax = AllTaxes.filter(el => el.type === "TAX_FIXED").reduce((sum, el) => sum + (el.numeric_value || 0), 0)
    const discountRate = formValues[keyDiscount] || 0;
    const fullPrice = roundValue(value*(1+rateTax/100) + fixedTax)
    const netPrice = roundValue(value*(1-discountRate/100)*(1+rateTax/100) + fixedTax)
    form.setFieldsValue({ [keyFullPrice]: fullPrice, [keyNetPrice]: netPrice });
  };

  const isBarcodeDisabled = mod === modules.EditStorageItem && currentRegister?.barcode !== undefined && currentRegister.barcode.length > 0;
  const isEdit = mod === modules.EditStorageItem;
  const hasAdvancedData = Boolean(
    currentRegister?.custom_id ||
    currentRegister?.barcode ||
    currentRegister?.sku ||
    sellTaxes.some(t => t.id > 0) ||
    purchaseTaxes.some(t => t.id > 0) ||
    otherValues.some(o => o.id > 0)
  );
  const defaultActiveCollapse = (isEdit && hasAdvancedData) ? ["advanced-options"] : [];

  return(
    <>
    {/* Campos Principales */}
    <Row gutter={[10, 10]}>
      <Col xs={24} sm={{span:20,offset:2}}>
        <Form.Item
          label="Nombre"
          name="name"
          rules={[{ required: true, message: 'Por favor ingresa el nombre' }]}
        >
          <Input placeholder="Ingresa el nombre del servicio"/>
        </Form.Item>
      </Col>

      {/* Galería de Imágenes */}
      <Col xs={24} sm={{ span: 20, offset: 2 }}>
        <Form.Item name="images" style={{ marginBottom: 12 }}>
          <ItemImageUploader
            elementId={currentRegister?.id}
            elementUid={currentRegister?.uid}
            value={formImages || currentRegister?.images || (currentRegister?.image ? [{ file_name: currentRegister.name || "Imagen principal", file_url: currentRegister.image, is_primary: true, display_order: 0, storage_path: "" }] : [])}
            onChange={(imgs, primaryUrl) => {
              form.setFieldsValue({
                images: imgs,
                image: primaryUrl || "",
                image_mini: primaryUrl || "",
              })
            }}
          />
        </Form.Item>
        <Form.Item name="image" hidden><Input /></Form.Item>
        <Form.Item name="image_mini" hidden><Input /></Form.Item>
      </Col>

      {checkedList.includes("SALE") && (
        <Col xs={24} sm={{ span: 20, offset: 2 }}>
          <Row gutter={[10, 10]}>
            <Col xs={24} sm={{ span: sellDiscountValue > 0 ? 8 : 12 }}>
              <Form.Item
                label="Precio de venta base"
                name="sell_price"
                rules={[{ required: true, message: 'Por favor ingresa el valor del item' }]}
              >
                <InputNumber
                  placeholder="Ingresa el valor"
                  prefix="$"
                  style={{ width: '100%' }}
                  formatter={formatInputNumber}
                  parser={parseInputNumber}
                  onChange={(val) => handlePriceChange("sell", { sell_price: val })}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={{ span: sellDiscountValue > 0 ? 8 : 12 }}>
              <Form.Item
                label="Precio de venta completo"
                name="sell_price_full"
                rules={[{ required: true, message: 'Por favor ingrese un valor' }]}
              >
                <InputNumber
                  placeholder="Ingresa el valor"
                  disabled
                  prefix="$"
                  style={{ width: '100%' }}
                  min={0}
                  formatter={formatInputNumber}
                  parser={parseInputNumber}
                />
              </Form.Item>
            </Col>
            {sellDiscountValue > 0 && (
              <Col xs={24} sm={{ span: 8 }}>
                <Form.Item
                  label="Precio con descuento"
                  name="sell_price_net"
                  rules={[{ required: true, message: 'Por favor ingrese un valor' }]}
                >
                  <InputNumber
                    placeholder="Ingresa el valor"
                    disabled
                    prefix="$"
                    style={{ width: '100%' }}
                    min={0}
                    formatter={formatInputNumber}
                    parser={parseInputNumber}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Col>
      )}

      {checkedList.includes("PURCHASE") && (
        <Col xs={24} sm={{ span: 20, offset: 2 }}>
          <Row gutter={[10, 10]}>
            <Col xs={24} sm={{ span: purchaseDiscountValue > 0 ? 8 : 12 }}>
              <Form.Item
                label="Precio de compra base (Costo)"
                name="purchase_price"
                rules={[{ required: true, message: 'Por favor ingresa el costo del servicio' }]}
              >
                <InputNumber
                  placeholder="Ingresa el valor"
                  prefix="$"
                  style={{ width: '100%' }}
                  formatter={formatInputNumber}
                  parser={parseInputNumber}
                  onChange={(val) => handlePriceChange("purchase", { purchase_price: val })}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={{ span: purchaseDiscountValue > 0 ? 8 : 12 }}>
              <Form.Item
                label="Precio de compra completo"
                name="purchase_price_full"
                rules={[{ required: true, message: 'Por favor ingrese un valor' }]}
              >
                <InputNumber
                  placeholder="Ingresa el valor"
                  disabled
                  prefix="$"
                  style={{ width: '100%' }}
                  min={0}
                  formatter={formatInputNumber}
                  parser={parseInputNumber}
                />
              </Form.Item>
            </Col>
            {purchaseDiscountValue > 0 && (
              <Col xs={24} sm={{ span: 8 }}>
                <Form.Item
                  label="Precio con descuento"
                  name="purchase_price_net"
                  rules={[{ required: true, message: 'Por favor ingrese un valor' }]}
                >
                  <InputNumber
                    placeholder="Ingresa el valor"
                    disabled
                    prefix="$"
                    style={{ width: '100%' }}
                    min={0}
                    formatter={formatInputNumber}
                    parser={parseInputNumber}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Col>
      )}

      <Col xs={24} sm={{span:20,offset:2}}>
        <Form.Item
          label="Descripción"
          name="description"
          rules={[{ required: true, message: 'Por favor ingresa la descripción' }]}
        >
          <TextArea placeholder="Ingresa la descripción" autoSize={{ minRows: 2, maxRows: 6 }}/>
        </Form.Item>
      </Col>
    </Row>

    {/* Sección Colapsable para Opciones Avanzadas */}
    <Row gutter={[10, 10]} style={{ marginTop: '0.5rem' }}>
      <Col xs={24} sm={{ span: 20, offset: 2 }}>
        <Collapse
          defaultActiveKey={defaultActiveCollapse}
          style={{ borderRadius: '8px', background: '#fafafa', border: '1px solid #d9d9d9' }}
          items={[
            {
              key: 'advanced-options',
              forceRender: true,
              label: (
                <span style={{ fontWeight: 600, color: '#1890ff', fontSize: '0.95rem' }}>
                  <SettingOutlined style={{ marginRight: '8px' }} />
                  Campos adicionales y opciones avanzadas
                </span>
              ),
              children: (
                <div style={{ padding: '0 8px' }}>
                  {/* Identificación y Codificación */}
                  <h6 style={{ fontWeight: 600, color: '#595959', marginBottom: '0.75rem' }}>Identificación y Codificación</h6>
                  <Row gutter={[10, 10]}>
                    <Col xs={24} sm={{ span: 8 }}>
                      <Form.Item label="Identificador" name="custom_id">
                        <Input placeholder="Ingresa identificador" disabled={mod === modules.EditStorageItem && currentRegister?.custom_id !== undefined && currentRegister?.custom_id?.length > 0} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={{ span: 8 }}>
                      <Form.Item label="Código de barras" name="barcode">
                        <Input
                          disabled={isBarcodeDisabled}
                          placeholder="Ingresa código de barras"
                          suffix={<BarcodeOutlined onClick={() => !isBarcodeDisabled && setIsScannerOpen(true)} style={{ cursor: isBarcodeDisabled ? "not-allowed" : "pointer", color: isBarcodeDisabled ? "#bfbfbf" : "#1890ff" }} />}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={{ span: 8 }}>
                      <Form.Item label="SKU" name="sku">
                        <Input placeholder="Ingresa sku" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider style={{ margin: '12px 0' }} />

                  {/* Categorización */}
                  <h6 style={{ fontWeight: 600, color: '#595959', marginBottom: '0.75rem' }}>Categorización</h6>
                  <Row gutter={[10, 10]}>
                    <Col xs={24} sm={{ span: 12 }}>
                      <Form.Item label="Categoría" name="category">
                        <Select placeholder="Selecciona categoría">
                          {categories.map((v) => <Option key={v.id} value={v.key}>{v.string_value}</Option>)}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider style={{ margin: '12px 0' }} />

                  {/* Impuestos y Descuentos */}
                  {(checkedList.includes("SALE") || checkedList.includes("PURCHASE")) && (
                    <>
                      <h6 style={{ fontWeight: 600, color: '#595959', marginBottom: '0.75rem' }}>Impuestos y Descuentos</h6>
                      <Row gutter={[10, 10]}>
                        {checkedList.includes("SALE") && (
                          <Col xs={24} sm={{ span: 12 }}>
                            <Form.Item
                              label="Descuento Venta (%)"
                              name="sell_discount"
                              tooltip="Porcentaje de descuento aplicable entre 0% y 100% (Ej: 10 para 10%)"
                              rules={[{ required: true, message: 'Por favor ingrese un valor' }]}
                            >
                              <InputNumber
                                placeholder="0% (Ej. 10)"
                                onChange={(val) => handlePriceChange("sell", { sell_discount: val })}
                                suffix="%"
                                min={0}
                                max={100}
                                formatter={formatInputNumber}
                                parser={parseInputNumber}
                                style={{ width: '100%' }}
                              />
                            </Form.Item>
                            {sellTaxes.map((impuesto, index) => (
                              <Row key={impuesto.id} gutter={[10, 10]} align="middle">
                                <Col span={16}>
                                  <Form.Item label={index === 0 ? "Impuesto Venta" : ""} name={`sell_taxes_${impuesto.id}`}>
                                    <Select
                                      allowClear
                                      placeholder="Selecciona impuesto"
                                      disabled={isFieldDisabled(impuesto.id, "sell_taxes")}
                                      onChange={(val) => handlePriceChange("sell", { [`sell_taxes_${impuesto.id}`]: val })}
                                    >
                                      {sellTaxesOptions.map((element) => (
                                        <Option key={element.key} value={element.key} disabled={disabledOption(element.key, "sell_taxes")}>{element.string_value}</Option>
                                      ))}
                                    </Select>
                                  </Form.Item>
                                </Col>
                                <Col span={8} style={{ marginTop: index === 0 ? "16px" : "-12px", display: "flex", alignItems: "center", gap: "8px" }}>
                                  {impuesto.id > 0 && (
                                    <Form.Item name={`sell_taxes_active_${impuesto.id}`} valuePropName="checked" style={{ margin: 0, paddingTop: '8px' }}>
                                      <Checkbox onChange={(e) => handlePriceChange("sell", { [`sell_taxes_active_${impuesto.id}`]: e.target.checked })} />
                                    </Form.Item>
                                  )}
                                  {sellTaxes.length > 0 ? (
                                    <MinusCircleOutlined onClick={() => removeSellTaxes(impuesto.id)} />
                                  ) : null}
                                </Col>
                              </Row>
                            ))}
                            {sellTaxesWarning && <Alert message="Seleccione un valor antes de añadir otro campo" banner closable onClose={() => setSellTaxesWarning(false)} />}
                            <Button type="dashed" size="small" style={{ marginTop: "5px" }} onClick={() => scanForEmptyFields(sellTaxes, setSellTaxes, setSellTaxesWarning, 'sell_taxes')}>Agregar Impuesto Venta</Button>
                          </Col>
                        )}

                        {checkedList.includes("PURCHASE") && (
                          <Col xs={24} sm={{ span: 12 }}>
                            <Form.Item
                              label="Descuento Compra (%)"
                              name="purchase_discount"
                              tooltip="Porcentaje de descuento aplicable entre 0% y 100% (Ej: 10 para 10%)"
                              rules={[{ required: true, message: 'Por favor ingrese un valor' }]}
                            >
                              <InputNumber
                                placeholder="0% (Ej. 10)"
                                onChange={(val) => handlePriceChange("purchase", { purchase_discount: val })}
                                suffix="%"
                                min={0}
                                max={100}
                                formatter={formatInputNumber}
                                parser={parseInputNumber}
                                style={{ width: '100%' }}
                              />
                            </Form.Item>
                            {purchaseTaxes.map((impuesto, index) => (
                              <Row key={impuesto.id} gutter={[10, 10]} align="middle">
                                <Col span={16}>
                                  <Form.Item label={index === 0 ? "Impuesto Compra" : ""} name={`purchase_taxes_${impuesto.id}`}>
                                    <Select
                                      allowClear
                                      placeholder="Selecciona impuesto"
                                      disabled={isFieldDisabled(impuesto.id, "purchase_taxes")}
                                      onChange={(val) => handlePriceChange("purchase", { [`purchase_taxes_${impuesto.id}`]: val })}
                                    >
                                      {purchaseTaxesOptions.map((element) => (
                                        <Option key={element.key} value={element.key} disabled={disabledOption(element.key, "purchase_taxes")}>{element.string_value}</Option>
                                      ))}
                                    </Select>
                                  </Form.Item>
                                </Col>
                                <Col span={8} style={{ marginTop: index === 0 ? "16px" : "-12px", display: "flex", alignItems: "center", gap: "8px" }}>
                                  {impuesto.id > 0 && (
                                    <Form.Item name={`purchase_taxes_active_${impuesto.id}`} valuePropName="checked" style={{ margin: 0, paddingTop: '8px' }}>
                                      <Checkbox onChange={(e) => handlePriceChange("purchase", { [`purchase_taxes_active_${impuesto.id}`]: e.target.checked })} />
                                    </Form.Item>
                                  )}
                                  {purchaseTaxes.length > 0 ? (
                                    <MinusCircleOutlined onClick={() => removePurchaseTaxes(impuesto.id)} />
                                  ) : null}
                                </Col>
                              </Row>
                            ))}
                            {purchaseTaxesWarning && <Alert message="Seleccione un valor antes de añadir otro campo" banner closable onClose={() => setPurchaseTaxesWarning(false)} />}
                            <Button type="dashed" size="small" style={{ marginTop: "5px" }} onClick={() => scanForEmptyFields(purchaseTaxes, setPurchaseTaxes, setPurchaseTaxesWarning, 'purchase_taxes')}>Agregar Impuesto Compra</Button>
                          </Col>
                        )}
                      </Row>
                      <Divider style={{ margin: '12px 0' }} />
                    </>
                  )}

                  {/* Datos Adicionales Personalizados */}
                  <h6 style={{ fontWeight: 600, color: '#595959', marginBottom: '0.75rem' }}>Datos Adicionales Personalizados</h6>
                  {otherValues.map((valor, index) => (
                    <Row key={valor.id} gutter={[10, 10]} align="middle">
                      <Col span={10}>
                        <Form.Item label={index === 0 ? "Nombre de dato" : ""} name={`otherValues_type_${valor.id}`}>
                          <Input placeholder="Ingrese el nombre" style={{ width: '100%' }} disabled={isFieldDisabled(valor.id, "otherValues_visible")} />
                        </Form.Item>
                      </Col>
                      <Col span={10}>
                        <Form.Item label={index === 0 ? "Valor de dato" : ""} name={`otherValues_value_${valor.id}`}>
                          <Input placeholder="Ingrese el valor" style={{ width: '100%' }} disabled={isFieldDisabled(valor.id, "otherValues_visible")} />
                        </Form.Item>
                      </Col>
                      <Col span={4} style={{ marginTop: index === 0 ? "20px" : "-8px", display: "flex", alignItems: "center", gap: "8px" }}>
                        {valor.id > 0 && (
                          <Form.Item name={`otherValues_visible_${valor.id}`} valuePropName="checked" style={{ margin: 0, paddingTop: '6px' }}>
                            <Checkbox />
                          </Form.Item>
                        )}
                        {otherValues.length > 0 ? (
                          <MinusCircleOutlined onClick={() => removeOtherValues(valor.id)} />
                        ) : null}
                      </Col>
                    </Row>
                  ))}
                  {otherValuesWarning && <Alert message="Seleccione un valor antes de añadir otro campo" banner closable onClose={() => setOtherValuesWarning(false)} />}
                  <Button type="dashed" size="small" style={{ marginTop: "5px" }} onClick={() => scanForEmptyFields(otherValues, setOtherValues, setOtherValuesWarning, "otherValues")}>Agregar Valor</Button>
                </div>
              )
            }
          ]}
        />
      </Col>
    </Row>

    <BarcodeScannerModal 
      visible={isScannerOpen} 
      onCancel={() => setIsScannerOpen(false)} 
      onScan={(result) => {
        form.setFieldsValue({ barcode: result });
      }}
    />
    </>
  )
}

export default RegisterServiceForm